import React, {useEffect, useState} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import Config from "../../core/config";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import ConfirmButton from "../common/ConfirmButton";
import BackButton from "../common/BackButton";
import Cookies from "js-cookie";
import {useNavigate} from "react-router-dom";

const apiUrl = Config.apiUrl;
const ADMIN = 2;

function loggedUser() {
    try {
        return JSON.parse(Cookies.get('user') || '{}');
    } catch (error) {
        return {};
    }
}

const UserManager = () => {
    const [, setLoading] = useRecoilState(loadingAtom);
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');
    const currentUser = loggedUser();
    const isAdmin = currentUser.userType === ADMIN;

    const newEntity = {
        id: undefined,
        name: '',
        email: '',
        password: '',
        userType: 0,
        uuid: '',
        // Não admin: o backend sempre grava na empresa de quem está cadastrando.
        companyId: currentUser.companyId || 0
    };

    const [formData, setFormData] = useState(newEntity);
    const [confirmPassword, setConfirmPassword] = useState('');
    const [uuidCopied, setUuidCopied] = useState(false);
    const [companies, setCompanies] = useState([]);

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) : value)
        }));
    };

    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
    };

    async function makeQuery() {
        if (id) {
            try {
                const url = apiUrl + '/api/users/id=' + id;
                const response = await makeRequest(url, 'GET', {'Useruuid': Cookies.get('userUUID')});
                // Não exibir a senha ao editar
                response.password = '';
                setFormData(response);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        } else {
            await generateUuidForNewUser();
        }
    }

    async function generateUuidForNewUser() {
        setUuidCopied(false);
        try {
            const url = apiUrl + '/api/users/new-uuid';
            const response = await makeRequest(url, 'GET', {'Useruuid': Cookies.get('userUUID')});
            setFormData({...newEntity, uuid: response.uuid});
        } catch (error) {
            console.error('Error generating uuid:', error);
            setFormData(newEntity);
        }
    }

    const copyUuid = async () => {
        try {
            await navigator.clipboard.writeText(formData.uuid || '');
            setUuidCopied(true);
            setTimeout(() => setUuidCopied(false), 2000);
        } catch (error) {
            console.error('Error copying uuid:', error);
        }
    };

    const createUuid = async () => {
        try {
            const url = apiUrl + '/api/users/new-uuid';
            const response = await makeRequest(url, 'GET', {'Useruuid': Cookies.get('userUUID')});
            setFormData((prevData) => ({...prevData, uuid: response.uuid}));
        } catch (error) {
            console.error('Error creating uuid:', error);
            alert('Erro ao criar UUID.');
        }
    };

    useEffect(() => {
        makeQuery();
    }, [id]);

    useEffect(() => {
        if (!isAdmin) {
            return;
        }
        makeRequest(apiUrl + '/api/companies', 'GET', {})
            .then(response => setCompanies(Array.isArray(response) ? response : []))
            .catch(error => console.error('Error fetching companies:', error));
    }, [isAdmin]);

    function clearEntity() {
        setFormData(newEntity);
        setConfirmPassword('');
    }

    const onConfirm = async () => {

        try {
            if (validateInputs()) {
                setLoading(true);
                await new Promise(resolve => setTimeout(resolve, 300));
                await saveEntity();
                navigate('/userlist');
            }
        } catch (error) {
            console.error('Error saving:', error);
            alert('Error ao salvar.');
        } finally {
            setLoading(false);
        }
    };

    function validateInputs() {

        if (!formData.name || formData.name === '') {
            alert('Preencha o campo nome.');
            return false;
        }

        if (!formData.email || formData.email === '') {
            alert('Preencha o campo email.');
            return false;
        }

        // Validação de senha apenas para novos usuários ou se a senha foi alterada
        if (!id || (formData.password && formData.password !== '')) {
            if (!formData.password || formData.password === '') {
                alert('Preencha o campo senha.');
                return false;
            }

            if (formData.password !== confirmPassword) {
                alert('As senhas não coincidem.');
                return false;
            }
        }

        if (isAdmin && !(formData.companyId > 0)) {
            alert('Selecione a empresa.');
            return false;
        }

        if (formData.userType < 0 || formData.userType > 2) {
            alert('Tipo de usuário inválido.');
            return false;
        }

        return true;
    }

    async function saveEntity() {
        const userUUID = Cookies.get('userUUID');
        const url = apiUrl + '/api/users';
        
        // Se for edição e não foi informada nova senha, remover o campo password
        const dataToSend = {...formData};
        if (id && (!dataToSend.password || dataToSend.password === '')) {
            delete dataToSend.password;
        }
        
        await makeRequest(url, id ? 'PUT' : 'POST', {
            'Content-Type': 'application/json',
            'Useruuid': userUUID,
        }, JSON.stringify(dataToSend));
    }

    return (
        <div>
            <div className="header-label">{formData.id > 0 ? 'Editar' : 'Cadastrar'} Usuário</div>

            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>UUID:</div>
                {formData.uuid ? (
                    <>
                        <input type="text" style={{width: '300px'}} value={formData.uuid} name="uuid" readOnly/>
                        <button type="button" style={{marginLeft: '8px'}} onClick={copyUuid}>
                            {uuidCopied ? 'Copiado!' : 'Copiar'}
                        </button>
                    </>
                ) : (
                    <button type="button" onClick={createUuid}>Criar UUID</button>
                )}
            </div>

            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Nome:</div>
                <input type="text" style={{width: '300px'}} value={formData.name} name="name"
                       onChange={handleChange}/>
            </div>

            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Email:</div>
                <input type="email" style={{width: '300px'}} value={formData.email} name="email"
                       onChange={handleChange}/>
            </div>

            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Senha:</div>
                <input type="password" style={{width: '300px'}} value={formData.password} name="password"
                       onChange={handleChange} placeholder={id ? "Deixe em branco para manter a senha atual" : ""}/>
            </div>

            {(!id || (formData.password && formData.password !== '')) && (
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Confirmar Senha:</div>
                    <input type="password" style={{width: '300px'}} value={confirmPassword} name="confirmPassword"
                           onChange={handleConfirmPasswordChange}/>
                </div>
            )}

            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Tipo de Usuário:</div>
                <select style={{width: '300px'}} value={formData.userType} name="userType"
                        onChange={handleChange}>
                    <option value={0}>Básico</option>
                    <option value={1}>Intermediário</option>
                    <option value={2}>Admin</option>
                </select>
            </div>

            {isAdmin && (
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Empresa:</div>
                    <select style={{width: '300px'}} value={formData.companyId || ''} name="companyId"
                            onChange={(e) => setFormData((prevData) => ({
                                ...prevData,
                                companyId: parseInt(e.target.value, 10) || 0
                            }))}>
                        <option value="">Selecione...</option>
                        {companies.map(company => (
                            <option key={company.id} value={company.id}>
                                {company.fantasyName || company.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}


            <ActionButtonsManagerView>
                <BackButton onClick={() => navigate(-1)}/>
                <ConfirmButton onClick={onConfirm}/>
            </ActionButtonsManagerView>


        </div>
    );
};

export default UserManager;

